import {setLoggingData} from 'src/redux/slices/authSlice'
import type {AppDispatch} from 'src/redux/store'
import {loggedInAdminAccessToken, logOutHitCount} from 'src/utils/constants'
import {
  clearAllSessionsAndPrepareLogin,
  hasValidAdminSnapshot,
  IMPERSONATION_SESSION_FLAG,
} from 'src/utils/impersonationTokens'

let sessionLogoutInProgress = false

export const SESSION_EXPIRED_MESSAGE_KEY = 'session_expired_message'

export function isInvalidTokenApiError(error: any): boolean {
  if (error?.response?.status !== 401) return false

  const data = error?.response?.data ?? {}
  const code = data.code
  const detail = String(data.detail ?? data.message ?? data.massage ?? '').toLowerCase()

  return (
    code === 'token_not_valid' ||
    detail.includes('token not valid') ||
    detail.includes('given token not valid')
  )
}

export function consumeSessionExpiredMessage(): string | null {
  const message = sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY)
  if (message) {
    sessionStorage.removeItem(SESSION_EXPIRED_MESSAGE_KEY)
  }
  return message
}

/** Removes legacy logout gate left by older builds. */
export function clearStaleLogoutGate(): void {
  localStorage.removeItem(logOutHitCount)
}

function redirectToLoginAfterClear(dispatch: AppDispatch, message?: string): void {
  if (sessionLogoutInProgress) return
  sessionLogoutInProgress = true

  clearAllSessionsAndPrepareLogin()
  dispatch(setLoggingData({isLoggedIn: false, userData: null}))

  if (message) {
    sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_KEY, message)
  }

  window.location.assign('/auth')
}

/** Full logout when the active customer session token is rejected. */
export function performSessionExpiredLogout(
  dispatch: AppDispatch,
  message = 'Session is expired. Please login again'
): void {
  redirectToLoginAfterClear(dispatch, message)
}

/** Logout when the frozen admin snapshot is rejected during impersonation. */
export function performAdminSessionExpiredLogout(dispatch: AppDispatch): void {
  redirectToLoginAfterClear(
    dispatch,
    'Your admin session has ended. Please sign in again.'
  )
}

/**
 * When impersonation is active but the admin snapshot is missing or expired,
 * clear customer tokens too and redirect to login.
 */
export function logoutIfImpersonationAdminExpired(dispatch: AppDispatch): boolean {
  const impersonationFlag = sessionStorage.getItem(IMPERSONATION_SESSION_FLAG) === '1'
  const hasAdminSnapshot = Boolean(localStorage.getItem(loggedInAdminAccessToken))

  // Do not force logout for normal sessions just because a stale admin snapshot key exists.
  // Forced admin-expired logout is only valid while an impersonation session is active.
  if (!impersonationFlag) {
    return false
  }

  if (!hasAdminSnapshot) {
    performAdminSessionExpiredLogout(dispatch)
    return true
  }

  if (hasValidAdminSnapshot()) {
    return false
  }

  performAdminSessionExpiredLogout(dispatch)
  return true
}
